import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useViewFeaturesStore } from '@/store/viewFeaturesStore';
import { paintTerrainArea } from '@/utils/viewFeatures';

export interface PaintHandlerParams {
	canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
	cameraRef: React.MutableRefObject<THREE.Camera | null>;
	sceneRef: React.MutableRefObject<THREE.Scene | null>;
	sceneInitialized: boolean;
}

export const usePaintHandler = (params: PaintHandlerParams) => {
	const { canvasRef, cameraRef, sceneRef, sceneInitialized } = params;
	const isPaintingRef = useRef(false);
	const paintPointsRef = useRef<THREE.Vector3[]>([]);
	const previewLineRef = useRef<THREE.Object3D | null>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const camera = cameraRef.current;
		const scene = sceneRef.current;

		if (!canvas || !camera || !scene) {
			return;
		}

		const raycaster = new THREE.Raycaster();
		const mouse = new THREE.Vector2();

		const getTerrainIntersectionPoint = (event: MouseEvent): THREE.Vector3 | null => {
			const rect = canvas.getBoundingClientRect();
			mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
			mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

			raycaster.setFromCamera(mouse, camera);

			// Only intersect with terrain
			const terrainObjects: THREE.Object3D[] = [];
			scene.traverse((child: THREE.Object3D) => {
				if (child.userData.category === 'terrain') {
					terrainObjects.push(child);
				}
			});

			const intersects = raycaster.intersectObjects(terrainObjects, true);
			if (intersects.length > 0) {
				return intersects[0].point;
			}

			return null;
		};

		const updatePreviewLine = () => {
			if (!scene) return;

			// Remove old preview line
			if (previewLineRef.current) {
				scene.remove(previewLineRef.current);
			if (previewLineRef.current instanceof THREE.Mesh || previewLineRef.current instanceof THREE.Line) {
				previewLineRef.current.geometry.dispose();
				if (previewLineRef.current.material instanceof THREE.Material) {
					previewLineRef.current.material.dispose();
				}
			}
		}

		const { currentPaintType } = useViewFeaturesStore.getState();
		const points = paintPointsRef.current;

		if (points.length < 2) return;

		// Create preview line as a tube for better visibility
		const path = new THREE.CatmullRomCurve3(points);
		const tubeGeometry = new THREE.TubeGeometry(path, points.length * 2, 0.4, 8, false);
		const material = new THREE.MeshBasicMaterial({
			color: currentPaintType === 'water' ? 0x0066ff :
				currentPaintType === 'park' ? 0x66bb6a : 0x00ff00,
			transparent: true,
			opacity: 0.7,
			side: THREE.DoubleSide,
		});

		const tube = new THREE.Mesh(tubeGeometry, material);
		tube.name = '__paintPreview__';
		scene.add(tube);
		previewLineRef.current = tube;
	};

	const handleMouseDown = (event: MouseEvent) => {
		// Only respond to left-click (button 0)
		if (event.button !== 0) return;

		const { isPaintMode } = useViewFeaturesStore.getState();
		if (!isPaintMode) return;

		// Prevent camera rotation when painting
		event.preventDefault();
		event.stopPropagation();

		const point = getTerrainIntersectionPoint(event);
		if (point) {
			isPaintingRef.current = true;
			paintPointsRef.current = [point];
		}
	};

	const handleMouseMove = (event: MouseEvent) => {
		if (!isPaintingRef.current) return;

		// Prevent camera rotation while painting
		event.preventDefault();
		event.stopPropagation();

		const point = getTerrainIntersectionPoint(event);
		if (point) {
			// Add point if it's far enough from the last point (throttle)
			const lastPoint = paintPointsRef.current[paintPointsRef.current.length - 1];
			if (!lastPoint || point.distanceTo(lastPoint) > 2) {
				paintPointsRef.current.push(point);
				updatePreviewLine();
			}
		}
	};

	const handleMouseUp = (event: MouseEvent) => {
			const point = getTerrainIntersectionPoint(event);
			if (point) {
				paintPointsRef.current.push(point);
			}

			// Complete the painting if we have enough points
			if (paintPointsRef.current.length >= 3) {
				const { currentPaintType } = useViewFeaturesStore.getState();
				paintTerrainArea(paintPointsRef.current, currentPaintType, scene);
			}

			// Clean up
			isPaintingRef.current = false;
			paintPointsRef.current = [];
			
			if (previewLineRef.current) {
				scene.remove(previewLineRef.current);
				if (previewLineRef.current instanceof THREE.Mesh || previewLineRef.current instanceof THREE.Line) {
					previewLineRef.current.geometry.dispose();
					if (previewLineRef.current.material instanceof THREE.Material) {
						previewLineRef.current.material.dispose();
					}
				}
				previewLineRef.current = null;
			}
		};

		const handleMouseLeave = () => {
			// Cancel painting if mouse leaves canvas
			if (isPaintingRef.current) {
				isPaintingRef.current = false;
				paintPointsRef.current = [];
				
				if (previewLineRef.current && scene) {
					scene.remove(previewLineRef.current);
					if (previewLineRef.current instanceof THREE.Mesh || previewLineRef.current instanceof THREE.Line) {
						previewLineRef.current.geometry.dispose();
						if (previewLineRef.current.material instanceof THREE.Material) {
							previewLineRef.current.material.dispose();
						}
					}
					previewLineRef.current = null;
				}
			}
		};

		// Add event listeners with capture phase to intercept before camera controls
		canvas.addEventListener('mousedown', handleMouseDown, true);
		canvas.addEventListener('mousemove', handleMouseMove, true);
		canvas.addEventListener('mouseup', handleMouseUp, true);
		canvas.addEventListener('mouseleave', handleMouseLeave);

		return () => {
			// Clean up event listeners
			canvas.removeEventListener('mousedown', handleMouseDown, true);
			canvas.removeEventListener('mousemove', handleMouseMove, true);
			canvas.removeEventListener('mouseup', handleMouseUp, true);
			canvas.removeEventListener('mouseleave', handleMouseLeave);

			// Clean up preview line
			if (previewLineRef.current && scene) {
				scene.remove(previewLineRef.current);
				if (previewLineRef.current instanceof THREE.Mesh || previewLineRef.current instanceof THREE.Line) {
					previewLineRef.current.geometry.dispose();
					if (previewLineRef.current.material instanceof THREE.Material) {
						previewLineRef.current.material.dispose();
					}
				}
			}
		};
	}, [canvasRef, cameraRef, sceneRef, sceneInitialized]);
};
